import { System } from '../System';
import { Entity } from '../Entity';
import { TransformComponent } from '../components/TransformComponent';
import { HealthComponent } from '../components/HealthComponent';
import { SpriteComponent } from '../components/SpriteComponent';
import { BeastieComponent } from '../components/BeastieComponent';
import { EventBus } from '../../utils/EventBus';

/**
 * Dying entity state for fade animation
 */
interface DyingEntity {
  entityId: string;
  fadeTimer: number;
  fadeDuration: number;
  xpAwarded: boolean;
  killedBy: string;
}

/**
 * DeathSystem - Handles death states for all entities
 * - Enemies: fade out, award XP, trigger loot, destroy
 * - Party members: transition to fainted state
 * - Player: trigger game over
 */
export class DeathSystem extends System {
  public readonly requiredComponents = ['health'];

  /** Entities currently in dying state */
  private dyingEntities: Map<string, DyingEntity> = new Map();

  /** Fade duration for enemies in ms */
  private fadeDuration = 1000;

  /** Loot drop chance (0-1) */
  private lootDropChance = 0.3;

  /** Track last attacker for XP attribution */
  private lastAttacker: Map<string, string> = new Map();

  public init(): void {
    // Listen for combat attacks to track who killed whom
    EventBus.onEvent('combat:attack', (data) => {
      this.lastAttacker.set(data.targetId, data.attackerId);
    });
  }

  public update(entities: Entity[], delta: number): void {
    // Process dying entities (fade animation)
    this.updateDyingEntities(delta);

    // Check for newly dead entities
    for (const entity of entities) {
      const health = entity.getComponent<HealthComponent>('health');
      if (!health) continue;

      // Skip if already being processed
      if (this.dyingEntities.has(entity.id)) continue;

      // Check if entity just died
      if (!health.isAlive && !health.isFainted) {
        this.handleDeath(entity, health);
      }
    }
  }

  /**
   * Handle entity death based on type
   */
  private handleDeath(entity: Entity, health: HealthComponent): void {
    const killedBy = this.lastAttacker.get(entity.id) || 'unknown';

    // Determine entity type
    if (entity.hasTag('player')) {
      this.handlePlayerDeath(entity);
    } else if (entity.hasTag('party_member')) {
      this.handlePartyMemberDeath(entity, health);
    } else if (entity.hasTag('enemy') || entity.hasTag('wild')) {
      this.handleEnemyDeath(entity, killedBy);
    }
  }

  /**
   * Handle player death - trigger game over
   */
  private handlePlayerDeath(entity: Entity): void {
    const transform = entity.getComponent<TransformComponent>('transform');

    EventBus.emitEvent('entity:died', {
      entityId: entity.id,
      killedBy: this.lastAttacker.get(entity.id) || 'unknown',
      entityType: 'player',
    });

    EventBus.emitEvent('player:died', {
      position: { x: transform?.x || 0, y: transform?.y || 0 },
    });

    EventBus.emitEvent('game:over', { reason: 'Player defeated' });
  }

  /**
   * Handle party member death - transition to fainted
   */
  private handlePartyMemberDeath(entity: Entity, health: HealthComponent): void {
    const beastie = entity.getComponent<BeastieComponent>('beastie');

    // Set to fainted state instead of dead
    health.isFainted = true;
    health.isAlive = true; // Keep alive but incapacitated

    // Gray out the sprite
    const sprite = entity.getComponent<SpriteComponent>('sprite');
    if (sprite) {
      sprite.setTint(0x666666); // Gray tint for fainted
      sprite.alpha = 0.7;
      sprite.sprite?.setAlpha(0.7);
    }

    EventBus.emitEvent('entity:fainted', {
      entityId: entity.id,
      speciesId: beastie?.speciesId || 'unknown',
    });

    EventBus.emitEvent('entity:died', {
      entityId: entity.id,
      killedBy: this.lastAttacker.get(entity.id) || 'unknown',
      entityType: 'party',
    });

    // Check if all party members are now fainted
    this.checkAllPartyFainted();
  }

  /**
   * Check if all party members are fainted - triggers game over
   */
  private checkAllPartyFainted(): void {
    const partyMembers = this.world.getEntitiesByTag('party_member');

    // No party members means no game over from this check
    if (partyMembers.length === 0) return;

    // Check if any party member is still able to fight
    const hasActiveMember = partyMembers.some((member) => {
      const health = member.getComponent<HealthComponent>('health');
      return health && health.isAlive && !health.isFainted;
    });

    if (!hasActiveMember) {
      EventBus.emitEvent('game:over', { reason: 'All party members fainted' });
    }
  }

  /**
   * Handle enemy death - fade out, award XP, loot, destroy
   */
  private handleEnemyDeath(entity: Entity, killedBy: string): void {
    // Start dying state
    this.dyingEntities.set(entity.id, {
      entityId: entity.id,
      fadeTimer: 0,
      fadeDuration: this.fadeDuration,
      xpAwarded: false,
      killedBy,
    });

    // Emit death event
    EventBus.emitEvent('entity:died', {
      entityId: entity.id,
      killedBy,
      entityType: 'enemy',
    });
  }

  /**
   * Update dying entities (fade animation and cleanup)
   */
  private updateDyingEntities(delta: number): void {
    const toRemove: string[] = [];

    for (const [entityId, dying] of this.dyingEntities) {
      const entity = this.world.getEntity(entityId);
      if (!entity) {
        toRemove.push(entityId);
        continue;
      }

      dying.fadeTimer += delta;
      const progress = Math.min(dying.fadeTimer / dying.fadeDuration, 1);

      // Update sprite alpha for fade effect
      const sprite = entity.getComponent<SpriteComponent>('sprite');
      if (sprite) {
        sprite.alpha = 1 - progress;
      }

      // Award XP once at start of fade
      if (!dying.xpAwarded) {
        this.awardXP(entity, dying.killedBy);
        this.rollLootDrop(entity);
        dying.xpAwarded = true;
      }

      // Remove entity when fade complete
      if (progress >= 1) {
        this.world.destroyEntity(entityId);
        toRemove.push(entityId);
      }
    }

    // Clean up removed entities
    for (const id of toRemove) {
      this.dyingEntities.delete(id);
      this.lastAttacker.delete(id);
    }
  }

  /**
   * Award XP to party members
   */
  private awardXP(deadEntity: Entity, _killedBy: string): void {
    const beastie = deadEntity.getComponent<BeastieComponent>('beastie');
    if (!beastie) return;

    // Calculate base XP
    const baseXP = beastie.level * 10;

    // Get living party members to split XP
    const partyMembers = this.world.getEntitiesByTag('party_member');
    const livingMembers = partyMembers.filter((e) => {
      const health = e.getComponent<HealthComponent>('health');
      return health && health.isAlive && !health.isFainted;
    });

    if (livingMembers.length === 0) return;

    const xpPerMember = Math.floor(baseXP / livingMembers.length);

    for (const member of livingMembers) {
      const memberBeastie = member.getComponent<BeastieComponent>('beastie');
      if (memberBeastie) {
        const leveledUp = memberBeastie.addExperience(xpPerMember);

        EventBus.emitEvent('xp:awarded', {
          recipientId: member.id,
          amount: xpPerMember,
          sourceId: deadEntity.id,
        });

        if (leveledUp) {
          EventBus.emitEvent('beastie:levelUp', {
            id: member.id,
            newLevel: memberBeastie.level,
          });
        }
      }
    }
  }

  /**
   * Roll for loot drop
   */
  private rollLootDrop(entity: Entity): void {
    if (Math.random() > this.lootDropChance) return;

    const transform = entity.getComponent<TransformComponent>('transform');
    if (!transform) return;

    // For now, emit event with placeholder items
    // Future: integrate with inventory system
    EventBus.emitEvent('loot:dropped', {
      position: { x: transform.x, y: transform.y },
      items: ['berry'], // Placeholder
    });
  }

  public onEntityRemoved(entity: Entity): void {
    // Clean up tracking
    this.dyingEntities.delete(entity.id);
    this.lastAttacker.delete(entity.id);
  }

  public destroy(): void {
    this.dyingEntities.clear();
    this.lastAttacker.clear();
  }
}
